import { useCallback, useEffect, useState } from 'react';
import { openMetaOAuthPopup } from '../../../components/InstagramAutomation/openMetaOAuthPopup';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import {
    disconnectAdHubPlatform,
    exchangeAdHubMetaOAuth,
    fetchAdHubConnections,
    fetchAdHubMetaOAuthUrl,
} from '../../../redux/adhub/actions';
import {
    adHubApiErrorMessage,
    findMetaConnection,
    getAdHubMetaOAuthRedirectUri,
    isMetaAdHubConnected,
    isMetaAdHubReadyForClientAds,
    normalizeAdAccountId,
} from '../../../utils/adhubConnections';
import type {
    IAdHubConnectionsResponse,
    IAdHubMetaAccountFormValues,
    IAdHubPlatformConnection,
} from '../../../types/global/adhubConnections.types';

export function useAdHubMetaConnection(brandId: string | undefined) {
    const dispatchPromise = useDispatchPromise();
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [connection, setConnection] = useState<IAdHubPlatformConnection | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pendingOAuthCode, setPendingOAuthCode] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        if (!brandId) {
            setConnection(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = (await dispatchPromise(fetchAdHubConnections(brandId))) as IAdHubConnectionsResponse;
            setConnection(findMetaConnection(data.connections));
        } catch (err: any) {
            const message = adHubApiErrorMessage(err, 'Failed to load AdHub connections');
            setError(message);
            setConnection(null);
        } finally {
            setLoading(false);
        }
    }, [brandId, dispatchPromise]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const startConnect = useCallback(async () => {
        if (!brandId) {
            showToastMessage('Select a brand first', 'error');
            return;
        }
        setConnecting(true);
        setError(null);
        try {
            const redirectUri = getAdHubMetaOAuthRedirectUri();
            const url = (await dispatchPromise(fetchAdHubMetaOAuthUrl({ brandId, redirectUri }))) as string;
            const code = await openMetaOAuthPopup(url);
            setPendingOAuthCode(code);
        } catch (err: any) {
            const message = adHubApiErrorMessage(err, 'Meta authorization failed');
            if (!/closed before completing|cancelled/i.test(message)) {
                showToastMessage(message, 'error');
            }
            setError(message);
        } finally {
            setConnecting(false);
        }
    }, [brandId, dispatchPromise]);

    const completeConnect = useCallback(
        async (values: IAdHubMetaAccountFormValues) => {
            if (!brandId || !pendingOAuthCode) {
                showToastMessage('Restart Meta connect to continue', 'error');
                return false;
            }
            const accountId = normalizeAdAccountId(values.platform_account_id);
            const pageId = values.facebook_page_id.trim();
            if (!accountId || !pageId) {
                showToastMessage('Ad account ID and Facebook Page ID are required', 'error');
                return false;
            }
            setConnecting(true);
            try {
                const redirectUri = getAdHubMetaOAuthRedirectUri();
                await dispatchPromise(
                    exchangeAdHubMetaOAuth({
                        brandId,
                        body: {
                            code: pendingOAuthCode,
                            redirect_uri: redirectUri,
                            platform_account_id: accountId,
                            platform_account_name: values.platform_account_name.trim() || undefined,
                            metadata: {
                                facebook_page_id: pageId,
                                ...(values.instagram_user_id.trim()
                                    ? { instagram_user_id: values.instagram_user_id.trim() }
                                    : {}),
                            },
                        },
                    }),
                );
                setPendingOAuthCode(null);
                showToastMessage('Meta ad account connected', 'success');
                await refresh();
                return true;
            } catch (err: any) {
                const message = adHubApiErrorMessage(err, 'Failed to save Meta connection');
                showToastMessage(message, 'error');
                setError(message);
                return false;
            } finally {
                setConnecting(false);
            }
        },
        [brandId, pendingOAuthCode, refresh, dispatchPromise],
    );

    const cancelPendingConnect = useCallback(() => {
        setPendingOAuthCode(null);
    }, []);

    const disconnect = useCallback(async () => {
        if (!brandId) return;
        setDisconnecting(true);
        try {
            await dispatchPromise(disconnectAdHubPlatform({ brandId, platform: 'meta' }));
            showToastMessage('Meta ad account disconnected', 'success');
            setConnection(null);
            await refresh();
        } catch (err: any) {
            showToastMessage(adHubApiErrorMessage(err, 'Failed to disconnect Meta'), 'error');
        } finally {
            setDisconnecting(false);
        }
    }, [brandId, refresh, dispatchPromise]);

    return {
        loading,
        connecting,
        disconnecting,
        connection,
        error,
        pendingOAuthCode,
        connected: isMetaAdHubConnected(connection),
        readyForClientAds: isMetaAdHubReadyForClientAds(connection),
        refresh,
        startConnect,
        completeConnect,
        cancelPendingConnect,
        disconnect,
    };
}
